import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { Subscription } from 'rxjs';
import { EntityInterface, TableConstruct } from 'src/app/model/Table';
import * as XLSX from 'xlsx';

/**
 * TableComponent wraps the angular material table element
 */
@Component({
    selector: 'app-table',
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableComponent<Entity extends EntityInterface> implements
  AfterViewInit, OnInit {
  /** The paginator to apply to the table */
  @ViewChild(MatPaginator, { static: true }) paginator!: MatPaginator;

  /** The sort element in our table */
  @ViewChild(MatSort, { static: false }) sort!: MatSort;

  /** How long it has been since the last refresh */
  public timeSinceLastRefresh: string = "0:00";

  /** Subscription to update new entities */
  private _changesSub: Subscription | null = null;

  /** Injected information about the construction of the table */
  @Input("construct") public construct!: TableConstruct<Entity>;
  public loading = true;

  constructor(private readonly changeDetectorRefs: ChangeDetectorRef) { }

  /**
   * Initialize the sort, paginator, and application data source
   */
  ngOnInit() {

    // Subscribe to timer changes
    this.construct.service.timeSinceLastRefresh$.subscribe(
      (str: string) => {
        this.timeSinceLastRefresh = str;
      });

    // Add a filter predicate to search with our custom toString method with delimieters
    this.construct.source.filterPredicate = (data: Entity, filter: string): boolean => {
      return data.toString(this.construct.columns).includes(filter);
    }

    // Get models from the service whenever the service changes
    this._changesSub = this.construct.service.changes$.subscribe(
      (data: EntityInterface[]) => {
        this.changeDetectorRefs.detectChanges();
        this._populateSource(data as Entity[]);
        this.loading = false;
      }
    );

    const data = this.construct.service.getEntities();
    if (data) {
      this._populateSource(data as Entity[]);
      this.loading = false;
    }
  }

  /**
   * After the table gets initialized, add a sorter and custom sorting functions
   */
  ngAfterViewInit() {
    // Add sort if enabled
    if (this.construct.settings.sort?.enabled) {
      this.construct.source.sort = this.sort;
    }

    // Add pagniator if enabled
    //if (this.construct.settings.paginator?.enabled) {
    this.construct.source.paginator = this.paginator;
    // }

    // Add sort accessor
    this.construct.source.sortingDataAccessor = (item: Entity, property: string): string | number => {
      return (item as any)[property];
    }
  }

  /**
   * When the component gets destroyed (user navigates away, unsubscribe from changes)
   */
  public ngOnDestroy() {
    this._changesSub?.unsubscribe();
  }

  /**
   * Apply a filter to the table
   *
   * @param event the event that triggered the filter
   */
  public applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.construct.source.filter = filterValue.trim().toLowerCase();
  }

  private exportDataToXlsx(fileName: string, dataMapper: (entity: Entity) => any): void {
    const filteredData = this.construct.source.data.map(dataMapper);
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(filteredData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, fileName);
  }

  public exportInvitationsToXlsx(): void {
    this.exportDataToXlsx('invitations.xlsx', (entity: Entity) => ({
      'Name': (entity as any)._name,
      'Business Name': (entity as any)._businessName,
      'Email': (entity as any)._email,
      'Sent Date': (entity as any)._lastSent
    }));
  }

  public exportApplicationsToXlsx(): void {
    this.exportDataToXlsx('applications.xlsx', (entity: Entity) => ({
      'Sent By': (entity as any)._sentBy,
      'Business Legal Name': (entity as any)._businessName,
      'Business DBA': (entity as any)._businessDBA,
      'Applied On': (entity as any)._appliedOn,
      'Status': (entity as any)._status,
    }));
  }

  public exportCustomersToXlsx(): void {
    this.exportDataToXlsx('customers.xlsx', (entity: Entity) => ({
      'ID': (entity as any)._accountId,
      'Business Name': (entity as any)._borrowerName,
      'Credit Limit': (entity as any)._accountLimit,
      'Available Credit': (entity as any)._availableBalance,
      'Status': (entity as any)._status,
    }));
  }

  public exportTransactionsToXlsx(): void {
    this.exportDataToXlsx('transactions.xlsx', (entity: Entity) => ({
      'ID': (entity as any)._id,
      'Business Name': (entity as any)._customerName,
      'Reviewed By User': (entity as any)._user,
      'Last Updated Date': (entity as any)._createdDate,
      'Amount': (entity as any)._amount,
      'Status': (entity as any)._status,
    }));
  }

  /**
   * Populate the data source iteratively so it doesn't lag the application
   *
   * @note I don't know if async is required, I'm just paranoid
   */
  private _populateSource(incoming: Entity[]): void {
    // Don't update the table when the user is not on the page => angular will be mad :(
    if (this.construct.fetchConstraints()) return;

    // Array is cloned so as not to trigger any side effects during manipulation.
    let source = [ ...this.construct.source.data ];
    const current = this.construct.source.data.map((x: Entity) => x.copyOf());

    // For each incoming entity, determine if it exists in the source.
    const update = () => {
      let sourceIndex = 0; // Using currentIndex isn't reliable because we need to decrement it

      for (let currentIndex = 0; currentIndex < current.length; currentIndex++) {
        for (let incomingIndex = 0; incomingIndex < incoming.length; incomingIndex++) {
          // If entity IDs are equal check for updates and remove from the lists
          if (incoming[incomingIndex].id === current[currentIndex].id) {

            // If the applications are not equal, update the current source to the new one
            if (!incoming[incomingIndex].equals(current[currentIndex])) {
              source[sourceIndex] = incoming[incomingIndex].copyOf() as Entity;
            }

            // We've updated the application, so remove it from both tracking arrays
            incoming.splice(incomingIndex, 1);
            current.splice(currentIndex, 1);

            currentIndex--;
            break;
          }
        }

        sourceIndex++;
      }
    }
    update();

    // Add new applications that we don't currently have
    const add = () => {
      for (let newApplication of incoming) {
        source.push(newApplication);
      }
    }
    add();

    // Remove old applications that have been removed
    const remove = () => {
      for (let oldApplication of current) {
        source = source.filter(x => x.id !== oldApplication.id);
      }
    }
    remove();

    // Update the table
    this.construct.source.data = source;
  }
}

function sendTheNewValue(event: Event | undefined) {
  throw new Error('Function not implemented.');
}

